<template>
  <div class="social-networks-preview-row">
    <ul class="social-networks-preview-row__list">
      <li class="social-networks-preview-row__logo-wrap" :style="logoWrapStyle" v-for="channel in uniqueChannels" :key="channel">
        <TooltipWrapper :disabled="props.tooltipDisabled">
          <template #hoverableElement>
            <button
              type="button"
              :title="getSocialChannelLabel(channel)"
              :aria-label="getSocialChannelLabel(channel)"
              :aria-pressed="props.selectedChannel === channel"
              :class="['social-networks-preview-row__logo-button', { 'social-networks-preview-row__logo-button--selected': props.selectedChannel === channel }]"
              @click="emit('toggleSelectedChannel', channel)"
            >
              <img
                :class="['social-networks-preview-row__logo', { 'social-networks-preview-row__logo--inactive': props.isUnusedPost }]"
                :src="`/images/social-logo-${channel.toLowerCase()}.svg`"
                :alt="getSocialChannelLabel(channel)"
              />
            </button>
          </template>
          <template #tooltip>{{ `${getSocialChannelLabel(channel)} preview` }}</template>
        </TooltipWrapper>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import { getSocialChannelLabel, SocialChannels, sortSocialChannelsForDisplay } from "@contracts/socialNetworks";
import type { SocialChannelData } from "@contracts/socialMediaSettings";

const props = withDefaults(
  defineProps<{
    socialChannels?: Array<SocialChannelData>;
    selectedChannel?: SocialChannels | null;
    logoSizePx?: number;
    isUnusedPost?: boolean;
    tooltipDisabled?: boolean;
  }>(),
  {
    socialChannels: () => [],
    selectedChannel: null,
    logoSizePx: 24,
    isUnusedPost: false,
    tooltipDisabled: false,
  }
);

const emit = defineEmits<{
  (e: "toggleSelectedChannel", channel: SocialChannels): void;
}>();

const uniqueChannels = computed<Array<SocialChannels>>(() => {
  const usedChannels = new Set<SocialChannels>();
  const deduped: Array<SocialChannels> = [];
  for (const channelItem of props.socialChannels) {
    const channelType = channelItem.channelType;
    if (usedChannels.has(channelType)) continue;
    usedChannels.add(channelType);
    deduped.push(channelType);
  }
  return sortSocialChannelsForDisplay(deduped);
});

const logoWrapStyle = computed(() => ({ width: `${props.logoSizePx}px` }));
</script>

<style scoped lang="scss">
.social-networks-preview-row {
  flex-shrink: 0;

  &__list {
    gap: 4px;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    list-style: none;
  }

  &__logo-wrap {
    width: 21px;
    display: flex;
    align-items: center;
  }

  &__logo-button {
    border: 1px solid transparent;
    padding: 1.5px;
    width: 100%;
    display: flex;
    cursor: pointer;
    background: transparent;
    border-radius: 8px;
    align-items: center;
    justify-content: center;
    transition:
      background-color 0.16s ease,
      border-color 0.16s ease;

    &--selected {
      background: rgba(0, 0, 0, 0.14);
      border-color: rgba(0, 0, 0, 0.2);
    }
  }

  &__logo {
    width: 100%;
    display: block;

    &--inactive {
      opacity: 0.4;
      filter: grayscale(1);
    }
  }
}
</style>
